import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import type { ApiErrorResponse } from "@/api/api.types";
import { PasswordRequirements } from "@/components/signUp";
import { usePasswordRecovery } from "@/hooks/usePasswordRecovery";
import type { ResetPasswordFormValuesType } from "@/types";
import { resetPasswordFormSchema } from "@/types";
import { Button, FormField, PasswordInput, Spinner } from "@/ui";
import { handleAxiosFieldErrors } from "@/utils";

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm<ResetPasswordFormValuesType>({
    resolver: zodResolver(resetPasswordFormSchema),
  });

  const { useResetPassword } = usePasswordRecovery();
  const { mutate: resetPassword, isPending } = useResetPassword();

  if (!token) return <p>Invalid reset link</p>;

  const onSubmit: SubmitHandler<ResetPasswordFormValuesType> = ({
    password,
  }) => {
    resetPassword(
      { password, token },
      {
        onError: (error: unknown) => {
          const axiosError = error as AxiosError<ApiErrorResponse>;

          handleAxiosFieldErrors(axiosError, setError);

          if (axiosError.response?.status === 403) {
            toast.error(
              "Reset password link has expired. Please request a new password reset",
            );
            return;
          }
          if (!axiosError.response?.data?.error?.fields) {
            toast.error("An unexpected error occurred. Please try again.");
          }
        },
      },
    );
  };

  const password = watch("password") ?? "";

  return (
    <>
      <h1 className="text-center text-2xl font-bold text-primary-950">
        Reset your password
      </h1>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-10">
        <FormField label="New password" error={errors.password?.message}>
          <PasswordInput
            placeholder="Enter your password"
            {...register("password")}
            error={errors.password?.message}
          />
        </FormField>

        <FormField
          label="Confirm new password"
          error={errors.passwordConfirmation?.message}
        >
          <PasswordInput
            placeholder="Enter your password"
            {...register("passwordConfirmation")}
            error={errors.passwordConfirmation?.message}
          />
        </FormField>
        <PasswordRequirements password={password} />
        <Button className="my-8 w-full font-semibold" type="submit">
          {isPending ? <Spinner /> : "Save new password"}
        </Button>
      </form>
    </>
  );
};
